import {
    Attribute,
    Component,
    Input,
    Output,
    EventEmitter,
    ViewChild,
    AfterViewInit,
    HostBinding,
    OnInit,
    SimpleChanges,
    OnChanges
} from '@angular/core';
import { MatSlider, MatSliderModule } from '@angular/material/slider';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { FormsModule } from '@angular/forms';
import { FlexModule } from '@angular/flex-layout/flex';
import { DateHandlerService } from 'crimson/lib/date-service/date-handler.service';
import { DateTimeUtils } from 'crimson/lib/date-service/date-utils';
import { TimeUnit } from 'crimson/lib/date-service/date.constants';
import { NvMatSliderKeyboardFixupDirective } from '../../directives/nv-mat-keyboard-fixups.directive';

export enum NvHigSliderTextType {
    Integer = 'integer',
    Decimal = 'decimal',
    Time = 'time',
    Percent = 'percent'
}

interface InputValidationPattern {
    editing: RegExp;
    committed: RegExp;
}

const InputValidationPatterns: Record<NvHigSliderTextType, InputValidationPattern> = {
    [NvHigSliderTextType.Integer]: {
        editing: /^-?\d*$/,
        committed: /^-?\d+$/
    },
    [NvHigSliderTextType.Decimal]: {
        editing: /^-?\d*\.?\d*$/,
        committed: /^-?(?:\d+(?:\.\d*)?|\.\d+)$/
    },
    [NvHigSliderTextType.Time]: {
        editing: /^\d{0,2}(?::\d{0,2})?$/,
        committed: /^\d{1,2}:\d{1,2}$/
    },
    [NvHigSliderTextType.Percent]: {
        editing: /^-?\d*(?: | %)?$/,
        committed: /^-?\d+(?: %)?$/
    }
};

@Component({
    selector: 'nv-hig-slider',
    templateUrl: './nv-hig-slider.component.html',
    styleUrls: ['./nv-hig-slider.component.scss'],
    imports: [FlexModule, MatSliderModule, NvMatSliderKeyboardFixupDirective, FormsModule]
})
export class NvHigSliderComponent implements AfterViewInit, OnInit, OnChanges {
    @Input()
    get min(): number {
        return this._min;
    }
    set min(val: number) {
        this._min = coerceNumberProperty(val, this._min);
    }
    private _min = 0;

    @Input()
    get max(): number {
        return this._max;
    }
    set max(val: number) {
        this._max = coerceNumberProperty(val, this._max);
    }
    private _max = 100;

    @Input()
    get step(): number {
        return this._step;
    }
    set step(val: number) {
        this._step = coerceNumberProperty(val, this._step);
    }
    private _step = 1;

    @Input()
    get default(): number {
        return this._default;
    }
    set default(val: number) {
        this._default = coerceNumberProperty(val, this._default);
    }
    private _default = undefined;

    @Input() disabled = false;
    @Input() thumbLabel = false;
    @Input() animating = false;
    @Input() ariaLabel = '';

    @Input()
    get tickInterval() {
        return this._tickInterval;
    }
    set tickInterval(value: 'auto' | number) {
        if (value === 'auto') {
            this._tickInterval = 'auto';
        } else if (typeof value === 'number' || typeof value === 'string') {
            this._tickInterval = coerceNumberProperty(value, this._tickInterval as number);
        } else {
            this._tickInterval = 0;
        }
        console.log('tickinterval: ', this._tickInterval);
    }
    private _tickInterval: 'auto' | number = 0;

    @Input()
    get value() {
        return this._value;
    }
    set value(val: number) {
        const newValue = coerceNumberProperty(val, this._value);
        if (this._value === newValue) {
            return;
        }
        this._value = newValue;
    }
    private _value = 0;

    @Output() valueChange: EventEmitter<number> = new EventEmitter<number>();
    @Output() readonly inputValueChange: EventEmitter<number> = new EventEmitter<number>();

    @Input() valueEntry = false;

    @Input()
    set formatTextAs(val: NvHigSliderTextType | string) {
        this._formatTextAs = (<any>NvHigSliderTextType)[val] || NvHigSliderTextType.Integer;
    }
    get formatTextAs() {
        return this._formatTextAs;
    }
    private _formatTextAs: NvHigSliderTextType = NvHigSliderTextType.Integer;

    get showTickMarks(): boolean {
        return !!this.tickInterval && this.tickInterval !== 0;
    }

    @ViewChild('slider', { static: true }) slider: MatSlider;
    @HostBinding('tabindex') realTabIndex: string;
    tabindex: number | undefined = undefined;

    inputValue: string = '';
    private lastAcceptedInputValue = '';
    textFocused = false;
    mouseDown = false;
    initializing = true;

    constructor(
        @Attribute('tabindex') tab: string,
        private dateHandlerService: DateHandlerService
    ) {
        this.tabindex = parseInt(tab, 10);
        if (Number.isNaN(this.tabindex)) {
            this.tabindex = undefined;
        }
        this.realTabIndex = '-1';
    }

    ngOnInit() {
        // Causes for NG0100 Error:
        // 1. Step property was being updated in forceTickUpdate method via multiple ways, which was causing NG0100 Error
        // 2. Value property was being updated in AfterViewInit life cycle hook, it should be updated in OnInit life cycle hook
        // Set initial text value.
        this.value = this.formatValue(this.value);
        this.updateInputTextValue(this.value);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['value']) {
            // Added this due to some sliders receives value from API, so in that case it should detect the change and format the value accordingly.
            this.value = this.formatValue(this.value);
            this.updateInputTextValue(this.value);
        }
    }

    ngAfterViewInit() {
        setTimeout(() => (this.initializing = false), 100);
    }

    onTextFocus() {
        this.textFocused = true;
    }

    onTextBlur() {
        this.textFocused = false;
    }

    textInput(value: string, input: HTMLInputElement): void {
        if (this.isValidInputValue(value, 'editing')) {
            this.inputValue = value;
            this.lastAcceptedInputValue = value;
            return;
        }

        this.inputValue = this.lastAcceptedInputValue;
        input.value = this.lastAcceptedInputValue;
    }

    textChanged(event: Event): void {
        event.stopPropagation();
        const input = event.target as HTMLInputElement;
        const parsedValue = this.parseInputValue(input.value);
        if (parsedValue === undefined) {
            this.restoreInputValue(event);
            return;
        }

        this.value = this.formatValue(parsedValue, false);
        this.updateInputTextValue(this.value);
        (event.target as HTMLInputElement).value = this.inputValue;
        this.inputValueChange.emit(this.value);
    }

    sliderChanged() {
        this.updateInputTextValue(this.value);
        this.valueChange.emit(Number(this.value));
    }

    private formatValue(value: number, ngChange: boolean = true): number {
        if (!Number.isFinite(value)) {
            return this._value;
        }
        let updatedVal = this.formatTextAs === NvHigSliderTextType.Decimal ? value : Math.trunc(value);

        if (this.step && !ngChange) {
            // Do not round off to nearest step value when value is changed from API. This preserves the default value.
            // removed multiplication with 100, as it was not needed.
            // Math.round is used to round the value to the nearest step. Ex = 0.37 value
            updatedVal = Math.round((updatedVal - this.min) / this.step) * this.step + this.min;
        }

        updatedVal = Math.min(this.max, Math.max(updatedVal, this.min));
        return updatedVal;
    }

    private updateInputTextValue(value: number) {
        if (this.formatTextAs === NvHigSliderTextType.Time) {
            const timeOffset = new Date(0).getTimezoneOffset();
            let val = new Date(timeOffset * 60 * 1000);
            val = DateTimeUtils.addTime(val, value, TimeUnit.SECONDS);
            this.inputValue = this.dateHandlerService.formatDate(val, 'mm:ss');
        } else if (this.formatTextAs === NvHigSliderTextType.Percent) {
            this.inputValue = value + ' %';
        } else if (this.formatTextAs === NvHigSliderTextType.Decimal) {
            // Format to 1 decimal place if it's a whole number, otherwise show actual decimals
            this.inputValue = value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
        } else {
            this.inputValue = value + '';
        }
        this.lastAcceptedInputValue = this.inputValue;
    }

    private restoreInputValue(event: Event): void {
        this.updateInputTextValue(this.value);
        (event.target as HTMLInputElement).value = this.inputValue;
    }

    onKeyDown(event: KeyboardEvent) {
        if (this.default && event.code === 'Period') {
            this.value = this.default;
            this.sliderChanged();
        }
    }

    private isValidInputValue(value: string, phase: keyof InputValidationPattern): boolean {
        return InputValidationPatterns[this.formatTextAs][phase].test(value);
    }

    private parseInputValue(value: string): number | undefined {
        if (!this.isValidInputValue(value, 'committed')) {
            return undefined;
        }

        let normalizedValue = value;

        switch (this.formatTextAs) {
            case NvHigSliderTextType.Time:
                if (!this.dateHandlerService.isValidDateTime(value, 'mm:ss')) {
                    return undefined;
                }
                return DateTimeUtils.convertTimeStringToSeconds(`00:${value}`);
            case NvHigSliderTextType.Percent:
                normalizedValue = value.endsWith(' %') ? value.slice(0, -2) : value;
                break;
        }

        const parsedValue = Number(normalizedValue);
        return Number.isFinite(parsedValue) ? parsedValue : undefined;
    }
}
